import React, { useEffect, useState, useRef } from "react";
import AxiosInstance from '../AxiosInstance';
import "./service.css";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
// Importation des composants enfants
import ServiceForm from "./components/Transaction/ServiceForm";
import ServiceList from "./components/Transaction/ServiceList";
import PricingManagement from "./components/Transaction/PricingManagement";
import StepsManagement from "./components/Transaction/StepsManagement";

const Transactions = () => {
  const [tab, setTab] = useState("create");
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [alert, setAlert] = useState({ type: "", message: "" });
  const [modalMessage, setModalMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    visible: false,
    message: "",
    onConfirm: null
  });

  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  // États partagés entre les composants
  const [currentServiceId, setCurrentServiceId] = useState(null);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [serviceEtapes, setServiceEtapes] = useState([]);
  const [serviceToEdit, setServiceToEdit] = useState(null);
  const [currentServiceData, setCurrentServiceData] = useState(null); // Données complètes du service actuel
  const [profil, setProfil] = useState(null);
  useEffect(() => {
    AxiosInstance.get('/api/users/profile')
      .then(response => setProfil(response.data))
      .catch(error => console.error(error));
  }, []);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  // Fonction utilitaire pour vérifier si le composant est monté
  const safeSetState = (setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  };

  // Chargement des données initiales
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [citiesRes, categoriesRes] = await Promise.all([
          AxiosInstance.get(`/api/cities`),
          AxiosInstance.get(`/api/categories`)
        ]);

        if (isMountedRef.current) {
          safeSetState(setCities, citiesRes.data.cities || citiesRes.data || []);
          const categoriesData = categoriesRes.data.typeOfCategories || categoriesRes.data.data || [];
          safeSetState(setCategories, Array.isArray(categoriesData) ? categoriesData : []);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données initiales:", error);
        if (isMountedRef.current) {
          safeSetState(setCities, []);
          safeSetState(setCategories, []);
        }
      }
    };

    fetchInitialData();
  }, []);

  // Fonctions utilitaires partagées
  const showModal = (message) => {
    setModalMessage(message);
    setTimeout(() => {
      if (isMountedRef.current) {
        setModalMessage("");
      }
    }, 3000);
  };

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => {
      if (isMountedRef.current) {
        setAlert({ type: "", message: "" });
      }
    }, 3000);
  };

  const showConfirmDialog = (message, onConfirm) => {
    setConfirmDialog({
      visible: true,
      message,
      onConfirm
    });
  };

  // Fonction pour démarrer l'édition d'un service (appelée depuis ServiceList)
  const startEditService = async (service) => {
    try {
      // Charger les détails complets du service avec ses localisations et tarifications
      const serviceResponse = await AxiosInstance.get(`/api/services/${service._id}`);
      const fullService = serviceResponse.data;

      // Stocker les données du service à éditer ET les données complètes pour les étapes
      setServiceToEdit(fullService);
      setCurrentServiceData(fullService); // Ajouter cette ligne
      setEditingServiceId(fullService.service._id);
      setCurrentServiceId(fullService.service._id);
      await loadServiceEtapes(fullService.service._id);

      // Changer d'onglet vers le formulaire
      setTab("create");
    } catch (error) {
      console.error("Erreur lors du chargement du service pour modification:", error);
      // En cas d'erreur, passer au moins les données de base
      setServiceToEdit({ service: service });
      setCurrentServiceData({ service: service, locations: [], pricing_data: [] });
      setEditingServiceId(service._id);
      setCurrentServiceId(service._id);
      loadServiceEtapes(service._id);
      setTab("create");
      showModal("Attention: Impossible de charger les localisations existantes. Vous devrez les reconfigurer.");
    }
  };

  // Fonction pour charger les données complètes d'un service (appelée depuis StepsManagement)
  const loadCurrentServiceData = async (serviceId) => {
    try {
      const serviceResponse = await AxiosInstance.get(`/api/services/${serviceId}`);
      const fullService = serviceResponse.data;
      setCurrentServiceData(fullService);
      return fullService;
    } catch (error) {
      console.error("Erreur lors du chargement des données du service:", error);
      setCurrentServiceData(null);
      return null;
    }
  };

  // Charger les étapes d'un service
  const loadServiceEtapes = async (serviceId) => {
    try {
      const response = await AxiosInstance.get(`/api/etapes/service/${serviceId}`);
      if (isMountedRef.current) {
        setServiceEtapes(response.data.data || []);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des étapes:", error);
      if (isMountedRef.current) {
        setServiceEtapes([]);
      }
    }
  };

  // Props communes pour tous les composants enfants
  const commonProps = {
    cities,
    categories,
    selectedCity,
    setSelectedCity,
    showModal,
    showAlert,
    showConfirmDialog,
    currentServiceId,
    setCurrentServiceId,
    editingServiceId,
    setEditingServiceId,
    serviceEtapes,
    setServiceEtapes,
    loadServiceEtapes,
    safeSetState,
    isMountedRef,
    setTab,
    startEditService,
    serviceToEdit,
    setServiceToEdit,
    currentServiceData,
    setCurrentServiceData,
    loadCurrentServiceData
  };

  return (
    <div className="container">
      <div className="header">
        <h1>Gestion des Services (Procédures)</h1>
        <p>Créez, gérez et tarifiez vos services avec étapes de processus</p>
      </div>

      <div className="tabs">
        <button
          className={`tab-button ${tab === "create" ? "active" : ""}`}
          onClick={() => setTab("create")}
        >
          {editingServiceId ? "Modifier le Service" : "Créer un Service"}
        </button>
        <button
          className={`tab-button ${tab === "list" ? "active" : ""}`}
          onClick={() => setTab("list")}
        >
          Liste des Services
        </button>
        {/* <button
          className={`tab-button ${tab === "pricing" ? "active" : ""}`}
          onClick={() => setTab("pricing")}
        >
          Gestion des Prix
        </button> */}
        <button
          className={`tab-button ${tab === "etapes" ? "active" : ""}`}
          onClick={() => setTab("etapes")}
        >
          Gestion des Étapes
        </button>
      </div>

      {alert.message && (
        <div className={`alert ${alert.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {alert.message}
        </div>
      )}

      {/* Rendu conditionnel des composants selon l'onglet actif */}
      {tab === "create" && (
        <ServiceForm {...commonProps} />
      )}

      {tab === "list" && (
        <ServiceList {...commonProps} />
      )}

      {/* {tab === "pricing" && (
        <PricingManagement {...commonProps} />
      )} */}

      {tab === "etapes" && (
        <StepsManagement {...commonProps} />
      )}

      {/* Modals globaux */}
      {modalMessage && (
        <div className="custom-modal">
          <div className="custom-modal-content">
            <p>{modalMessage}</p>
          </div>
        </div>
      )}

      {confirmDialog.visible && (
        <div className="custom-modal">
          <div className="custom-modal-content">
            <p>{confirmDialog.message}</p>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmDialog({ ...confirmDialog, visible: false })}
              >
                Annuler
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  confirmDialog.onConfirm();
                  setConfirmDialog({ ...confirmDialog, visible: false });
                }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;